import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AffiliateAccountStatus, AffiliateProfileResponse } from '@avanzu/types';
import { apiClient } from '../services/apiClient';

// Lightweight portal user — the affiliate portal has no organisation context
export interface PortalUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  emailVerified: boolean;
}

interface AuthState {
  user: PortalUser | null;
  affiliateStatus: AffiliateAccountStatus | null;
  loading: boolean;
}

enum AuthActionTypes {
  SET_LOADING = 'SET_LOADING',
  SET_AUTH = 'SET_AUTH',
  CLEAR_AUTH = 'CLEAR_AUTH',
}

type AuthAction =
  | { type: AuthActionTypes.SET_LOADING; payload: boolean }
  | {
      type: AuthActionTypes.SET_AUTH;
      payload: { user: PortalUser; affiliateStatus: AffiliateAccountStatus | null };
    }
  | { type: AuthActionTypes.CLEAR_AUTH };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AuthActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case AuthActionTypes.SET_AUTH:
      return {
        user: action.payload.user,
        affiliateStatus: action.payload.affiliateStatus,
        loading: false,
      };

    case AuthActionTypes.CLEAR_AUTH:
      return { user: null, affiliateStatus: null, loading: false };

    default:
      return state;
  }
}

const storage = {
  save(user: PortalUser, affiliateStatus: AffiliateAccountStatus | null) {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('affiliateStatus', affiliateStatus ?? '');
  },

  load(): { user: PortalUser; affiliateStatus: AffiliateAccountStatus | null } | null {
    try {
      const user = localStorage.getItem('user');
      if (!user) return null;
      const status = localStorage.getItem('affiliateStatus');
      return {
        user: JSON.parse(user),
        affiliateStatus: (status || null) as AffiliateAccountStatus | null,
      };
    } catch {
      return null;
    }
  },

  clear() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('affiliateStatus');
    sessionStorage.clear();
  },
};

interface AuthContextType {
  user: PortalUser | null;
  affiliateStatus: AffiliateAccountStatus | null;
  loading: boolean;
  login: (
    token: string,
    user: PortalUser,
    affiliateStatus: AffiliateAccountStatus | null
  ) => void;
  logout: () => void;
  refreshUserState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    affiliateStatus: null,
    loading: true,
  });

  const handleAuthFailure = () => {
    storage.clear();
    dispatch({ type: AuthActionTypes.CLEAR_AUTH });
    window.location.href = '/login';
  };

  useEffect(() => {
    apiClient.setAuthCallbacks({
      onAuthFailure: handleAuthFailure,
      onOrganisationInvalidated: () => {}, // No organisation context in the portal
    });
  }, []);

  // Load initial auth state, then refresh from the API (affiliate status can
  // change server-side, e.g. admin approval)
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
      return;
    }

    const stored = storage.load();
    if (stored) {
      dispatch({ type: AuthActionTypes.SET_AUTH, payload: stored });
    }
    refreshUserState().catch(() => {
      if (!stored) dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (
    token: string,
    user: PortalUser,
    affiliateStatus: AffiliateAccountStatus | null
  ) => {
    localStorage.setItem('token', token);
    storage.save(user, affiliateStatus);
    dispatch({ type: AuthActionTypes.SET_AUTH, payload: { user, affiliateStatus } });
  };

  const logout = () => {
    storage.clear();
    dispatch({ type: AuthActionTypes.CLEAR_AUTH });
  };

  const refreshUserState = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const response = await apiClient.get('/affiliate/profile');
    if (response.ok) {
      const data: AffiliateProfileResponse = await response.json();
      const user: PortalUser = {
        id: data.user.id,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        email: data.user.email,
        avatarUrl: data.user.avatarUrl,
        emailVerified: data.user.emailVerified,
      };
      storage.save(user, data.affiliateStatus);
      dispatch({
        type: AuthActionTypes.SET_AUTH,
        payload: { user, affiliateStatus: data.affiliateStatus },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        affiliateStatus: state.affiliateStatus,
        loading: state.loading,
        login,
        logout,
        refreshUserState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
